import React from 'react';
import { ModalShell } from './ModalShell';
import { Library, Plus, X, Eye, Edit3, Trash2 } from 'lucide-react';
import { Prompt } from '../types';

interface PromptLibraryModalProps {
  promptLibrary: Prompt[];
  onClose: () => void;
  createNewPrompt: () => void;
  viewPrompt: (prompt: Prompt) => void;
  editPrompt: (prompt: Prompt) => void;
  deletePrompt: (promptId: string) => void;
  onSelectPrompt: (promptId: string) => void;
  selectedPromptId: string;
}

export const PromptLibraryModal: React.FC<PromptLibraryModalProps> = ({
  promptLibrary,
  onClose,
  createNewPrompt,
  viewPrompt,
  editPrompt,
  deletePrompt,
  onSelectPrompt,
  selectedPromptId
}) => {
  return (
    <ModalShell labelledBy="prompt-library-title" onEscape={onClose} panelClassName="max-w-[950px] max-h-[90vh] p-5 sm:p-8">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h2 id="prompt-library-title" style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Library style={{ width: '20px', height: '20px', color: 'var(--accent-strong)' }} />
            Biblioteka szablonów AI
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={createNewPrompt}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--accent-strong)',
                color: 'var(--text-on-accent)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-strong)')}
            >
              <Plus style={{ width: '16px', height: '16px' }} />
              Nowy szablon
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
                color: 'var(--error)',
                border: '1px solid color-mix(in srgb, var(--error) 35%, transparent)',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 24%, transparent)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 12%, transparent)')}
            >
              <X style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
        </div>

        <div style={{
          flex: 1,
          overflow: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
          paddingBottom: '8px'
        }}>
          {promptLibrary.map(prompt => (
            <div key={prompt.id} style={{
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '20px',
              backgroundColor: 'var(--surface-card)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
              transition: 'background-color 0.2s'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
                  {prompt.name}
                </h3>
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  <button
                    onClick={() => viewPrompt(prompt)}
                    style={{
                      padding: '5px',
                      backgroundColor: 'color-mix(in srgb, var(--info) 8%, transparent)',
                      color: 'var(--info)',
                      border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--info) 16%, transparent)')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--info) 8%, transparent)')}
                    title="Podgląd"
                  >
                    <Eye style={{ width: '14px', height: '14px' }} />
                  </button>
                  <button
                    onClick={() => editPrompt(prompt)}
                    style={{
                      padding: '5px',
                      backgroundColor: 'color-mix(in srgb, var(--warning) 8%, transparent)',
                      color: 'var(--warning)',
                      border: '1px solid color-mix(in srgb, var(--warning) 25%, transparent)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--warning) 16%, transparent)')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--warning) 8%, transparent)')}
                    title="Edytuj"
                  >
                    <Edit3 style={{ width: '14px', height: '14px' }} />
                  </button>
                  {prompt.isCustom && (
                    <button
                      onClick={() => deletePrompt(prompt.id)}
                      style={{
                        padding: '5px',
                        backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
                        color: 'var(--error)',
                        border: '1px solid color-mix(in srgb, var(--error) 35%, transparent)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 24%, transparent)')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 12%, transparent)')}
                      title="Usuń"
                    >
                      <Trash2 style={{ width: '14px', height: '14px' }} />
                    </button>
                  )}
                </div>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5', flexGrow: 1 }}>
                {prompt.description}
              </p>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                <span style={{
                  fontSize: '9px',
                  color: prompt.isCustom ? 'var(--warning)' : 'var(--text-secondary)',
                  backgroundColor: prompt.isCustom ? 'color-mix(in srgb, var(--warning) 8%, transparent)' : 'var(--surface-subtle)',
                  border: `1px solid ${prompt.isCustom ? 'color-mix(in srgb, var(--warning) 25%, transparent)' : 'var(--border)'}`,
                  padding: '2px 8px',
                  borderRadius: '9999px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {prompt.isCustom ? 'Prompt własny' : 'Systemowy'}
                </span>
              </div>
              
              <div style={{ marginTop: '4px' }}>
                <button
                  onClick={() => {
                    onSelectPrompt(prompt.id);
                    onClose();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: selectedPromptId === prompt.id ? 'var(--accent-strong)' : 'var(--surface-subtle)',
                    color: selectedPromptId === prompt.id ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                    border: selectedPromptId === prompt.id ? 'none' : '1px solid var(--border)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    boxShadow: selectedPromptId === prompt.id ? '0 4px 10px rgba(16, 185, 129, 0.1)' : 'none',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (selectedPromptId !== prompt.id) {
                      e.currentTarget.style.backgroundColor = 'var(--border)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedPromptId !== prompt.id) {
                      e.currentTarget.style.backgroundColor = 'var(--surface-subtle)';
                    }
                  }}
                >
                  {selectedPromptId === prompt.id ? '✓ Aktywny' : 'Wybierz styl'}
                </button>
              </div>
            </div>
          ))}
        </div>
    </ModalShell>
  );
};
